import React from "react";
import { View, StyleSheet, Platform, Text } from "react-native";
import CrossPlatformDropdown from "../../../components/CrossPlatformDropdown";
import LhButton from "../../../components/LhButton";
import LHOverlay from "../../../components/LHOverlay";
import { DropdownItem } from "../../../types/observation";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";

interface Zone {
  ZONE_ID: number;
  ZONE: string;
}

interface Props {
  isVisible: boolean;
  onClose: () => void;
  zones: Zone[];
  frequencyOptions: DropdownItem[];
  beginOverlayFilters: {
    zone: Zone[];
    frequency: DropdownItem | null;
  };
  showValidation: boolean;
  handleBeginOverlayFilterChange: (
    filterType: "zone" | "frequency",
    value: DropdownItem | DropdownItem[] | null
  ) => void;
  onProceed: () => void;
}

const LeadershipWalkBegin: React.FC<Props> = ({
  isVisible,
  onClose,
  zones,
  frequencyOptions,
  beginOverlayFilters,
  showValidation,
  handleBeginOverlayFilterChange,
  onProceed,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const labelColor = isDark ? '#FFFFFF' : '#343A40';
  const requiredColor = isDark ? '#FFFFFF' : '#6C757D';
  const infoIconColor = isDark ? '#FFFFFF' : '#6C757D';

  return (
    <LHOverlay
      visible={isVisible}
      onDismiss={onClose}
      title="Leadership Walk Selection"
      size="medium"
    >
      <View style={styles.filterOverlayContainer}>
        {/* Zone Dropdown with custom label */}
        <View style={styles.responsiveDropdown}>
          <View style={styles.labelRow}>
            <View style={styles.labelLeft}>
              <Text style={[styles.fieldLabel, { color: labelColor }]}>Zone</Text>
              <MaterialIcons
                name="info-outline"
                size={16}
                color={infoIconColor}
                style={styles.infoIcon}
              />
            </View>
            <Text style={[styles.requiredText, { color: requiredColor }]}>*Required</Text>
          </View>
          <CrossPlatformDropdown
            label=""
            placeholder="Select Zone"
            selection="multi"
            childList={zones.map((zone) => ({
              id: zone.ZONE_ID.toString(),
              displayText: zone.ZONE,
            }))}
            selectedValue={
              beginOverlayFilters.zone.length > 0
                ? beginOverlayFilters.zone.map((zone) => ({
                    id: zone.ZONE_ID.toString(),
                    displayText: zone.ZONE,
                  }))
                : null
            }
            setSelectedValue={handleBeginOverlayFilterChange.bind(null, "zone")}
            helperText={
              showValidation && !beginOverlayFilters.zone.length
                ? "This field is required"
                : ""
            }
            feedbackType={
              showValidation && !beginOverlayFilters.zone.length
                ? "error"
                : "info"
            }
          />
        </View>

        {/* Frequency Dropdown with custom label */}
        <View style={styles.responsiveDropdown}>
          <View style={styles.labelRow}>
            <View style={styles.labelLeft}>
              <Text style={[styles.fieldLabel, { color: labelColor }]}>Frequency</Text>
              <MaterialIcons
                name="info-outline"
                size={16}
                color={infoIconColor}
                style={styles.infoIcon}
              />
            </View>
            <Text style={[styles.requiredText, { color: requiredColor }]}>*Required</Text>
          </View>
          <CrossPlatformDropdown
            label=""
            placeholder="Select Frequency"
            childList={frequencyOptions}
            selectedValue={beginOverlayFilters.frequency}
            setSelectedValue={handleBeginOverlayFilterChange.bind(
              null,
              "frequency"
            )}
            helperText={
              showValidation && !beginOverlayFilters.frequency
                ? "This field is required"
                : ""
            }
            feedbackType={
              showValidation && !beginOverlayFilters.frequency ? "error" : "info"
            }
          />
        </View>

        <View style={styles.filterOverlayButtons}>
          <LhButton
            text="Cancel"
            variant="secondary"
            size="small"
            onPress={onClose}
            isLoading={false}
            disabled={false}
            style={{ ...styles.filterOverlayButton, ...styles.cancelButton }}
          />
          <LhButton
            text="Proceed"
            variant="primary"
            size="small"
            onPress={onProceed}
            isLoading={false}
            disabled={false}
            style={styles.filterOverlayButton}
          />
        </View>
      </View>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  filterOverlayContainer: {
    padding: Platform.select({ web: 16, ios: 0 }),
  },
  responsiveDropdown: {
    width: "100%",
    marginBottom: 12,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  labelLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#343A40",
    marginRight: 6,
    fontFamily: FontFamily.regular,
  },
  infoIcon: {
    marginTop: 2,
  },
  requiredText: {
    color: "#6C757D",
    fontSize: 12,
    fontFamily: FontFamily.regular,
  },
  filterOverlayButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    gap: 10,
  },
  filterOverlayButton: {
    width: Platform.OS === "web" ? "15%" : "40%",
    fontSize: 16,
    fontFamily: FontFamily.regular,
  },
  cancelButton: {
    borderWidth: 0,
    backgroundColor: "transparent",
  },
});

export default LeadershipWalkBegin;